export const hasDuplicates = (array: Readonly<(string | number)[]>) => {
	return new Set(array).size !== array.length;
};

export const isIncrementing = <T>(arr: Readonly<T[]>): boolean => {
	for (let i = 0; i < arr.length - 1; i++) {
		const currentElt = arr[i];
		const nextElt = arr[i + 1];
		if (currentElt === undefined || nextElt === undefined) throw Error();
		// Compare elements. For numbers, check if current is <= next; for strings, use localeCompare for accurate comparison.
		if (typeof currentElt === "number" && typeof nextElt === "number") {
			if (currentElt > nextElt) {
				return false;
			}
		} else if (typeof currentElt === "string" && typeof nextElt === "string") {
			if (currentElt.localeCompare(nextElt) > 0) {
				return false;
			}
		} else {
			throw new Error("Array contains unsupported types");
		}
	}
	return true;
};

export function areArraysIdentical(
	arr1: Readonly<string[]>,
	arr2: Readonly<string[]>,
): boolean {
	const arr1Sorted = [...arr1].sort((aa, bb) => aa.localeCompare(bb));
	const arr2Sorted = [...arr2].sort((aa, bb) => aa.localeCompare(bb));

	if (arr1Sorted.length !== arr2Sorted.length) {
		return false;
	}

	for (let i = 0; i < arr1Sorted.length; i++) {
		if (arr1Sorted[i] !== arr2Sorted[i]) {
			return false;
		}
	}

	return true;
}

export function calculateDifferences<T extends bigint | number | string>(
	arr1: Readonly<T[]>,
	arr2: Readonly<T[]>,
): { toAdd: T[]; toDelete: T[]; toNothing: T[] } {
	const toAdd: T[] = [];
	const toDelete: T[] = [];
	const toNothing: T[] = [];

	const arr1Set = new Set(arr1);
	const arr2Set = new Set(arr2);

	// Find elements to add (elements in arr2 but not in arr1)
	for (const item of arr2) {
		if (!arr1Set.has(item)) {
			toAdd.push(item);
		}
	}

	// Find elements to delete (elements in arr1 but not in arr2)
	for (const item of arr1) {
		if (arr2Set.has(item)) {
			toNothing.push(item);
		} else {
			toDelete.push(item);
		}
	}

	return { toAdd, toDelete, toNothing };
}

export function calculateDifferencesObj<T>(
	arr1: Readonly<T[]>,
	arr2: Readonly<T[]>,
	keys: Readonly<(keyof T)[]>,
): { toAdd: T[]; toDelete: T[]; toNothing: T[] } {
	const toAdd: T[] = [];
	const toDelete: T[] = [];
	const toNothing: T[] = [];

	const getUnifiedValue = (item: T) => keys.map((key) => item[key]).join("_");

	const arr1KeysSet = new Set(arr1.map((item) => getUnifiedValue(item)));
	const arr2KeysSet = new Set(arr2.map((item) => getUnifiedValue(item)));

	// Find objects to add (objects in arr2 but not in arr1)
	for (const obj of arr2) {
		if (!arr1KeysSet.has(getUnifiedValue(obj))) {
			toAdd.push(obj);
		}
	}

	// Find objects to delete (objects in arr1 and not in arr2)
	for (const obj of arr1) {
		if (!arr2KeysSet.has(getUnifiedValue(obj))) {
			toDelete.push(obj);
		}
	}

	// Find objects to update (objects in arr2 that are in arr1)
	// NOTE: important to do it in this way because we want newest values for update
	for (const obj of arr2) {
		if (arr1KeysSet.has(getUnifiedValue(obj))) {
			toNothing.push(obj);
		}
	}

	return { toAdd, toDelete, toNothing };
}

export function groupByArr<T, K extends keyof T>(
	data: Readonly<T[]>,
	properties: Readonly<K[]>,
): Pick<T, K>[] {
	// Step 1: Remove unwanted properties
	const simplified = data.map((item) =>
		properties.reduce<Pick<T, K>>((acc, key) => {
			acc[key] = item[key];
			return acc;
			// @ts-expect-error - type casting is removed by eslint
		}, {}),
	);

	// Step 2: Remove duplicates
	const unique = Array.from(
		new Set(simplified.map((item) => JSON.stringify(item))),
	).map((str) => JSON.parse(str) as Pick<T, K>);

	return unique;
}

export function removeDuplicates(nums: Readonly<number[]>): number[] {
	return [...new Set(nums)];
}

export function removeDuplicatesStrings(sts: Readonly<string[]>): string[] {
	return [...new Set(sts)];
}

export function sortByNumberProp<T>(
	arr: T[],
	key: keyof T,
	direction: "asc" | "desc" = "asc",
): T[] {
	return [...arr].sort((a, b) => {
		const aVal = a[key];
		const bVal = b[key];

		if (typeof aVal !== "number" || typeof bVal !== "number") {
			throw new Error(`Property "${String(key)}" is not a number on all items`);
		}

		return direction === "asc" ? aVal - bVal : bVal - aVal;
	});
}
